import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  Answer,
  Attempt,
  Feedback,
  ProgressItem,
  RecentScoreItem,
  Result,
  SelfScoreType,
} from '@thiememeulenhoff/types';
import { map } from 'rxjs';
import { InteractionEndpointsService } from './interaction.endpoints.service';

@Injectable({
  providedIn: 'root',
})
export class InteractionService {
  readonly #http = inject(HttpClient);

  readonly #endpoints = inject(InteractionEndpointsService);

  public submitAttempt(attempt: Attempt) {
    const endpoint = this.#endpoints.attempt();
    return this.#http.post<undefined>(endpoint, attempt);
  }

  public saveDraft(draft: Attempt) {
    const endpoint = this.#endpoints.draft();
    return this.#http
      .post(endpoint, draft, { observe: 'response' })
      .pipe(map((response) => response?.status));
  }

  public submitSelfScore(selfScore: SelfScoreType) {
    const endpoint = this.#endpoints.selfScore();
    return this.#http.post<SelfScoreType>(endpoint, selfScore);
  }

  public closeAttempt(arrangementCode: string, contentIdentifier: string) {
    const endpoint = this.#endpoints.close(arrangementCode, contentIdentifier);
    return this.#http.put<void>(endpoint, undefined);
  }

  public getProgressById(
    arrangementCode: string,
    contentIdentifier: string,
    depth = 1,
  ) {
    const endpoint = this.#endpoints.progressById(
      arrangementCode,
      contentIdentifier,
    );
    return this.#http.get<ProgressItem>(endpoint, {
      params: { depth },
    });
  }

  public getProgressByParagraphPath(
    arrangementCode: string,
    paragraphPath: string,
  ) {
    const endpoint = this.#endpoints.progressByParagraphPath(
      arrangementCode,
      paragraphPath,
    );
    return this.#http.get<ProgressItem>(endpoint);
  }

  public getProgressForMostRecent = () => {
    const endpoint = this.#endpoints.recentScoreOverall();
    return this.#http.get<Array<RecentScoreItem>>(endpoint);
  };

  public getRecentProgressForMethod = (methodSlug: string) => {
    const endpoint = this.#endpoints.recentScoreForMethod(methodSlug);
    return this.#http.get<Array<RecentScoreItem>>(endpoint);
  };

  public getRecentProgressForArrangement = (
    arrangementCode: string,
    useThor = false,
  ) => {
    const endpoint = this.#endpoints.recentScoreForArrangement(
      arrangementCode,
      useThor,
    );
    return this.#http.get<Array<RecentScoreItem>>(endpoint);
  };

  public getResultById(arrangementCode: string, contentIdentifier: string) {
    const endpoint = this.#endpoints.resultById(
      arrangementCode,
      contentIdentifier,
    );
    return this.#http.get<Result>(endpoint);
  }

  public clearResultById(arrangementCode: string, contentIdentifier: string) {
    const endpoint = this.#endpoints.clearResultById(
      arrangementCode,
      contentIdentifier,
    );
    return this.#http.delete(endpoint);
  }

  public getAnswerById(arrangementCode: string, contentIdentifier: string) {
    const endpoint = this.#endpoints.answerById(
      arrangementCode,
      contentIdentifier,
    );
    return this.#http.get<Answer>(endpoint);
  }

  public getFeedbackById(arrangementCode: string, contentIdentifier: string) {
    const endpoint = this.#endpoints.feedbackById(
      arrangementCode,
      contentIdentifier,
    );

    return this.#http.get<Array<Feedback>>(endpoint);
  }

  public startSelfTest(arrangementCode: string, contentPath: string) {
    const endpoint = this.#endpoints.startSelfTest();
    return this.#http.post(endpoint, {
      arrangementCode,
      contentPath,
    });
  }

  public submitSelfTest(arrangementCode: string, contentPath: string) {
    const endpoint = this.#endpoints.submitSelfTest();
    return this.#http.post(endpoint, { arrangementCode, contentPath });
  }

  public completeSelfTest(arrangementCode: string, contentPath: string) {
    const endpoint = this.#endpoints.completeSelfTest();
    return this.#http.post(endpoint, { arrangementCode, contentPath });
  }

  public deleteSelfTest(arrangementCode: string, contentPath: string) {
    const endpoint = this.#endpoints.deleteSelfTest();
    return this.#http.delete(endpoint, {
      body: {
        arrangementCode,
        contentPath,
      },
    });
  }
}
